import { useState } from 'react';
import PropTypes from 'prop-types';
import { DocumentTextIcon, SparklesIcon } from '@heroicons/react/24/outline';

const PasteTransformFlow = ({ onSubmit, loading }) => {
  const [text, setText] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleTextChange = (e) => {
    const value = e.target.value;
    setText(value);
    setCharCount(value.length);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!text.trim() || loading) return;
    onSubmit(text.trim());
  };

  const placeholderText = `Paste your experience here. This could be:

• Your LinkedIn profile summary and experience
• An old resume (just the text)
• Notes about your work history
• Bullet points of your achievements

Example:
John Doe
Software Engineer at Tech Company
2020 - Present

- Built new features for the mobile app
- Worked on improving performance
- Collaborated with the design team`;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
          <DocumentTextIcon className="w-8 h-8 text-primary-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Paste Your Experience
        </h2>
        <p className="text-gray-600">
          I'll analyze your text and extract a structured resume. Don't worry about formatting - just paste everything you have.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="relative">
          <textarea
            value={text}
            onChange={handleTextChange}
            placeholder={placeholderText}
            disabled={loading}
            className="w-full h-80 px-4 py-3 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed font-mono text-sm"
          />

          <div className="absolute bottom-3 right-3 text-xs text-gray-400">
            {charCount.toLocaleString()} characters
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            <SparklesIcon className="w-4 h-4 inline mr-1" />
            AI will extract: contact info, experience, education, skills
          </div>

          <button
            type="submit"
            disabled={!text.trim() || loading}
            className="px-6 py-2.5 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Analyzing...
              </>
            ) : (
              <>
                <SparklesIcon className="w-5 h-5" />
                Transform to Resume
              </>
            )}
          </button>
        </div>
      </form>

      <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="font-medium text-blue-900 mb-2">Tips for best results:</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Include job titles, company names, and dates</li>
          <li>• Add specific achievements with numbers when possible</li>
          <li>• Include your skills and technologies used</li>
          <li>• Don't worry about perfect formatting - I'll clean it up</li>
        </ul>
      </div>
    </div>
  );
};

PasteTransformFlow.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool
};

export default PasteTransformFlow;
